import { useParams, Link } from 'react-router-dom';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <main className="pt-20 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已下架</p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            返回漫画列表
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = getRelatedComics(comic.id, comic.category, 4);
  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画免费阅读 | 韩漫之家`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    image: comic.coverImage,
    url: `https://hanmanzhijia.com/article/${comic.id}`,
    publisher: {
      '@type': 'Organization',
      name: '韩漫之家',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanzhijia.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '全部漫画', to: '/category/all' },
                { label: comic.title },
              ]}
            />
          </div>

          <article className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            <div className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24">
                <div className="aspect-[3/4] w-full rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-accent-500 whitespace-nowrap">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1 text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {formatViews(comic.views)} 阅读
                </span>
                <span className="flex items-center gap-1 text-foreground-500 whitespace-nowrap">
                  <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center"></i>
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                  <strong>作品简介</strong>
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                  开始阅读第一话
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                  收藏
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                  分享
                </button>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-heading text-foreground-950 mb-4">
                  <strong>章节列表</strong>
                  <span className="text-sm text-foreground-500 font-normal ml-2">共 {comic.chapters.length} 话</span>
                </h3>
                <div className="space-y-2">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between p-3.5 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updateDate}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedComics.length > 0 && (
            <section className="mt-16">
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                  相关<strong className="text-primary-500">推荐</strong>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}